<template>
  <div class="exit--question">
    <div class="text--center">
      <h1 class="mb--3" data-test-id="feedback-header">{{ $t(`exit.step1.${user.party}.heading`) }}</h1>
      <p class="text--small mb--4">{{ $t(`exit.step1.${user.party}.subheading`) }}</p>
    </div>
    <div class="layout--size-wide mx--auto">
      <div class="box">
        <div class="form__radio">
          <input v-model="exit.solved" type="radio" id="form-radio-1" value="yes"
            :data-wml-speech-command="$t('commands.yes')" />
          <label for="form-radio-1" data-test-id="radio-feedback-positive">
            <a-icon name="check" class="text--success me--1" />
            <span>{{ $t(`exit.step1.${user.party}.feedback.positive`) }}</span>
          </label>
        </div>
      </div>
      <div class="box">
        <div class="form__radio">
          <input v-model="exit.solved" type="radio" id="form-radio-2" value="no"
            :data-wml-speech-command="$t('commands.no')" />
          <label for="form-radio-2" data-test-id="radio-feedback-negative">
            <a-icon name="close" class="text--danger me--1" />
            <span>{{ $t(`exit.step1.${user.party}.feedback.negative`) }}</span>
          </label>
        </div>
        <div v-if="showIssues" class="box__content">
          <div class="exit__issues">
            <div class="form__group mt--4">
              <div v-for="item in issues" class="form__radio mb--3">
                <input v-model="issue" type="radio" :value="item.value" :id="`form-issue-${item.value}`" />
                <label class="font-weight--normal" :for="`form-issue-${item.value}`"
                  :data-test-id="`radio-issue-${item.value}`"><span v-html="item.label"></span></label>
              </div>
              <div class="form__radio mb--2">
                <input v-model="issue" type="radio" id="form-other" value="other" />
                <label class="font-weight--normal" for="form-other" data-test-id="radio-issue-other">
                  <span v-html="bracketsToSmall($t('exit.step1.anotherIssue'))"></span>
                </label>
              </div>
              <textarea ref="other" v-model="otherIssue" class="form__control" rows="5" autocomplete="off"
                data-test-id="feedback-comment" :disabled="issue !== 'other'"></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="text--center">
      <a-button variant="primary" :data-wml-speech-command="$t('commands.submitFeedback')" @click="submitHandler"
        data-test-id="button-submit-feedback">
        {{ $t(`exit.step1.${user.party}.submitFeedback`) }}
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useExitStore, useUserStore } from '@/stores'
import { useRepository } from '@/composables'
import { useTranslation } from 'i18next-vue'
import { mediator, messenger } from '@communication'
import { useSessionStore } from '../../../../stores'

const user = useUserStore()
const exit = useExitStore()

const { sendStats, sendWsEvent } = useRepository()

const { i18next } = useTranslation()

const other = ref(null)
const issues = ref([])
const issue = ref('')
const otherIssue = ref('')
const showIssues = computed(() => user.isAssistant && exit.solved === 'no' && issues.value.length)

const emit = defineEmits(['next'])

const bracketsToSmall = value => {
  const groups = value.match(/([^(]+)(\(([^)]+)\))?/)
  const small = groups[3] ? ` <small>(${groups[3]})</small>` : ''
  return groups[1].trim() + small
}

async function submitHandler() {
  const { isSolved } = exit
  const { id: partyId } = mediator.session.user
  const session = useSessionStore()
  const report = {
    partyId,
    account: session.info.account,
    team: session.info.team ?? '',
    license: session.info.license,
  }
  if (user.isAssistant) {
    report.assistantFeedback = isSolved;
    report.assistantReason1 = !isSolved && issue.value === '1';
    report.assistantReason2 = !isSolved && issue.value === '2';
    report.assistantReason3 = !isSolved && issue.value === '3';
    report.assistantReason4 = !isSolved && issue.value === '4';
    report.assistantReason5 = !isSolved && issue.value === 'other' ? otherIssue.value : '';
    sendWsEvent({
      type: 'feedback:report',
      feedbackSolved: isSolved,
      feedbackOther: otherIssue.value,
      feedbackIssue1: issue.value === issues.value[0].value,
      feedbackIssue2: issue.value === issues.value[1].value,
      feedbackIssue3: issue.value === issues.value[2].value,
      feedbackIssue4: issue.value === issues.value[3].value,
      account: session.info.account,
      team: session.info.team ?? '',
      license: session.info.license
    })
  } else if (user.isClient) {
    report.clientFeedback = isSolved
    sendWsEvent({
      type: 'feedback:report',
      feedbackSolved: isSolved,
      account: session.info.account,
      team: session.info.team ?? '',
      license: session.info.license
    })
  }
  await sendStats(report)
  if (!isSolved) {
    messenger.emit('log:dumpTrigger', { reason: 'negative-feedback' })
  }
  emit('next')
}

watch(issue, val => {
  if (val === 'other') {
    nextTick(() => {
      other.value.focus()
    })
  }
})

function getIssues() {
  Object.values(i18next.store.data[i18next.language].translation.exit.step1.issues).forEach((label, index) => {
    issues.value.push({
      label: bracketsToSmall(label),
      value: (index + 1).toString(),
    })
  })
}

onMounted(() => {
  getIssues()
})
</script>

<style lang="scss"></style>
